import { useContext, useRef, useState } from 'react';
import { axiosClient } from '../../../../../library/Service/axiosClient';
import { SmartContext } from '../../../../../library/Core/SmartContext';

interface Props {
    modalId: string;
    onSaved: (newUrl: string, uploadedAt: string) => void;
}

const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const ResumeManageModal = ({ modalId, onSaved }: Props) => {
    const { state } = useContext(SmartContext);
    const resumeFilePath: string = state.data.skillInfo?.resumeFilePath ?? '';
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [pendingUrl, setPendingUrl] = useState('');
    const [pendingFile, setPendingFile] = useState<{ name: string; size: number } | null>(null);
    const [error, setError] = useState('');

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const allowed = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
        if (!allowed.includes(file.type)) {
            setError('Only PDF and Word documents (.pdf, .doc, .docx) are allowed.');
            return;
        }

        setError('');
        setPendingFile(null);
        setPendingUrl('');
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('pdf_doc', file);
            const base = (process.env.REACT_APP_COMMON_API ?? '').replace(/\/$/, '');
            const res = await axiosClient().post(`${base}/upload/upload-file`, formData);
            setPendingUrl(res.data.link);
            setPendingFile({ name: file.name, size: file.size });
        } catch {
            setError('File upload failed. Please try again.');
        } finally {
            setUploading(false);
        }
    };

    const handleSave = async () => {
        if (!pendingUrl) return;
        setSaving(true);
        setError('');
        try {
            const base = (process.env.REACT_APP_COMMON_API ?? '').replace(/\/$/, '');
            const res = await axiosClient().put(`${base}/resume`, { resumeFilePath: pendingUrl });
            onSaved(pendingUrl, res.data.resumeUploadedAt ?? '');
            setPendingUrl('');
            setPendingFile(null);
            const el = document.getElementById(modalId);
            if (el) {
                const { Modal } = await import('bootstrap');
                Modal.getInstance(el)?.hide();
            }
        } catch {
            setError('Failed to save resume. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="modal fade" id={modalId} tabIndex={-1} aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header bg-dark text-white py-3 px-3">
                        <h5 className="modal-title font-18 font-600">Your Resume</h5>
                        <button type="button" className="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close" />
                    </div>
                    <div className="modal-body">
                        {/* Current resume display */}
                        {resumeFilePath ? (
                            <div className="mb-4 p-3 rounded-3 bg-light d-flex align-items-center gap-3">
                                <i className="bi bi-file-earmark-text fs-3 text-success" />
                                <a href={resumeFilePath} target="_blank" rel="noopener noreferrer" className="font-14 text-skyblue">
                                    Download Resume
                                </a>
                            </div>
                        ) : (
                            <div className="mb-4 p-3 rounded-3 bg-light text-center text-muted">
                                <i className="bi bi-file-earmark-x fs-3 d-block mb-2" />
                                <span className="font-14">No resume uploaded yet. Please upload your resume.</span>
                            </div>
                        )}

                        {/* Upload section */}
                        <div className="mb-3">
                            <label className="form-label font-14 font-500">
                                {resumeFilePath ? 'Replace Resume' : 'Upload Resume'}
                            </label>
                            <input
                                ref={fileInputRef}
                                type="file"
                                className="form-control"
                                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                onChange={handleFileChange}
                                disabled={uploading || saving}
                            />
                            <div className="form-text">Accepted formats: PDF, DOC, DOCX (max 25 MB)</div>
                        </div>

                        {uploading && (
                            <div className="text-muted font-14"><span className="spinner-border spinner-border-sm me-2" />Uploading...</div>
                        )}

                        {pendingFile && !uploading && (
                            <div className="d-flex align-items-center gap-3 p-3 rounded-3 bg-light mb-0">
                                <div className="bg-danger rounded-2 d-flex align-items-center justify-content-center text-white font-12 font-700 flex-shrink-0"
                                    style={{ width: 40, height: 40 }}>
                                    {pendingFile.name.split('.').pop()?.toUpperCase() ?? 'FILE'}
                                </div>
                                <div className="flex-grow-1 overflow-hidden">
                                    <div className="font-14 font-500 text-truncate">{pendingFile.name}</div>
                                    <div className="font-13 text-muted">{formatFileSize(pendingFile.size)}</div>
                                </div>
                                <i className="bi bi-check-circle-fill text-success fs-5" />
                            </div>
                        )}

                        {error && (
                            <div className="alert alert-danger py-2 font-13 mb-0 mt-3">{error}</div>
                        )}
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-outline-secondary" data-bs-dismiss="modal">Cancel</button>
                        <button
                            type="button"
                            className="btn btn-green"
                            onClick={handleSave}
                            disabled={!pendingUrl || saving}
                        >
                            {saving ? <><span className="spinner-border spinner-border-sm me-1" />Saving...</> : 'Save Resume'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ResumeManageModal;
